import { useEffect, useState } from 'react'
import { Save, AlertCircle, CheckCircle2, Loader2, Mail, User, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
// ── Super admin personal account ──────────────────────────────────────────────

interface AccountForm {
  email: string
  first_name: string
  last_name: string
  new_password: string
  confirm_password: string
}

export default function Profile() {
  const { t } = useTranslation()
  const [form, setForm] = useState<AccountForm>({
    email: '',
    first_name: '',
    last_name: '',
    new_password: '',
    confirm_password: '',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api
      .getAccount()
      .then((d) => {
        setForm((prev) => ({
          ...prev,
          email: d.email ?? '',
          first_name: d.first_name ?? '',
          last_name: d.last_name ?? '',
        }))
      })
      .catch(() => setError(t('profile.loadError', 'Impossible de charger le profil.')))
      .finally(() => setLoading(false))
  }, [t])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    setSuccess(false)
    setError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (form.new_password && form.new_password !== form.confirm_password) {
      setError(t('adminProfile.passwordMismatch', 'Les mots de passe ne correspondent pas.'))
      return
    }
    setSaving(true)
    setSuccess(false)
    setError(null)
    try {
      const payload: Record<string, string> = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
      }
      if (form.new_password) payload.new_password = form.new_password
      await api.updateAccount(payload)
      setSuccess(true)
      setForm((prev) => ({ ...prev, new_password: '', confirm_password: '' }))
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(getApiErrorKey(apiErr, 'adminProfile.saveError'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
          <User className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('profile.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('profile.subtitle')}</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {t(error, error)}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {t('profile.saveSuccess')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Identity */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('profile.sectionIdentity')}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.firstName')}
              </label>
              <input
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                maxLength={100}
                placeholder="Jean"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.lastName')}
              </label>
              <input
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                maxLength={100}
                placeholder="Dupont"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {t('profile.email')} *
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              disabled
              required
              maxLength={255}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </section>

        {/* Password change */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <Lock className="w-4 h-4" />
            {t('profile.sectionPassword')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('profile.passwordHint')}</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.newPassword')}
              </label>
              <input
                type="password"
                name="new_password"
                value={form.new_password}
                onChange={handleChange}
                minLength={form.new_password ? 8 : undefined}
                autoComplete="new-password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('profile.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                autoComplete="new-password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </section>

        {/* Save button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? t('adminProfile.saving') : t('adminProfile.save')}
          </button>
        </div>
      </form>
    </div>
  )
}
